import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Checkbox, Input, Message, Placeholder, SelectPicker, Toggle } from 'rsuite';
import DatePicker from '../components/shared/DatePicker';
import { Button, Modal } from '@nsi-advisory-services/components';
import { DndContext, KeyboardSensor, PointerSensor, closestCenter, useSensor, useSensors } from '@dnd-kit/core';
import { restrictToParentElement, restrictToVerticalAxis } from '@dnd-kit/modifiers';
import { SortableContext, arrayMove, sortableKeyboardCoordinates, useSortable, verticalListSortingStrategy } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';

import PageHeader from '../components/shared/PageHeader';
import Section from '../components/shared/Section';
import SurveyForm from '../components/surveys/SurveyForm';
import SurveyLinkButton from '../components/surveys/SurveyLinkButton';
import SendSurveyModal from '../components/surveys/SendSurveyModal';
import SurveyScheduleModal from '../components/surveys/SurveyScheduleModal';
import useUnsavedChangesGuard from '../hooks/useUnsavedChangesGuard';
import { showToast } from '../utils/toast';
import {
  useCloseSurvey,
  useDeleteSurvey,
  useDuplicateSurvey,
  useManagedSurvey,
  useReopenSurvey,
  useSaveSurvey,
  useSurveyImageUrl,
  useUploadSurveyImage,
} from '../hooks/useSurveys';
import {
  ANONYMOUS_NOTE,
  CONTENT_TYPES,
  QUESTION_KINDS,
  STATE_LABELS,
  blockIcon,
  blockLabel,
  conditionOps,
  describeCondition,
  formatDate,
  hasSignature,
  kindInfo,
  newBlock,
  newOption,
  opNeedsValue,
  publishProblem,
  questionNumbers,
  scheduleLabel,
} from '../lib/surveys';
import './SurveyEditorPage.css';

const toDraft = (survey) => ({
  title: survey?.title || '',
  description: survey?.description || '',
  content: Array.isArray(survey?.content) ? survey.content : [],
  closesOn: survey?.closesOn || null,
  anonymous: survey?.anonymous === true,
  shuffleQuestions: survey?.shuffleQuestions === true,
});

const errorText = (err, fallback) => String(err?.message || err || fallback);

const toIsoDate = (date) => {
  if (!(date instanceof Date) || Number.isNaN(date.getTime())) return null;
  const pad = (n) => String(n).padStart(2, '0');
  return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}`;
};

const fromIsoDate = (iso) => {
  if (!iso) return null;
  const [y, m, d] = String(iso).split('-').map(Number);
  return new Date(y, m - 1, d);
};

/*
  Conditions must point at an earlier question, at an answer it still offers. After a move, a
  delete or an option edit, any that no longer do are dropped. Returns [content, how many dropped].
*/
const fixConditions = (content) => {
  const seen = new Map();
  let dropped = 0;
  const next = content.map((block) => {
    let out = block;
    const c = block.condition;
    if (c?.questionId) {
      const source = seen.get(c.questionId);
      const ops = source ? conditionOps(source).map((o) => o.value) : [];
      const valueOk = !opNeedsValue(c.op)
        || (source?.kind === 'choice' ? source.options.some((o) => o.id === c.value) : Number(c.value) >= 1 && Number(c.value) <= 5);
      if (!source || !ops.includes(c.op) || !valueOk) {
        out = { ...block, condition: null };
        dropped += 1;
      }
    }
    if (out.type === 'question') seen.set(out.id, out);
    return out;
  });
  return [next, dropped];
};

/** The survey builder: settings, then the survey's blocks in order, each editable in place. */
export default function SurveyEditorPage() {
  const { surveyId } = useParams();
  const { data: survey, isLoading, error } = useManagedSurvey(surveyId);

  if (isLoading) {
    return (
      <div className="bdc-page">
        <Placeholder.Paragraph rows={8} active />
      </div>
    );
  }
  if (error || !survey) {
    return (
      <div className="bdc-page">
        <PageHeader crumbs={[{ label: 'Home', to: '/' }, { label: 'Surveys', to: '/surveys' }, { label: 'Survey' }]} title="Survey" />
        <Message type="error" showIcon>{errorText(error, 'That survey could not be loaded.')}</Message>
      </div>
    );
  }
  return <Editor key={survey.id} survey={survey} />;
}

function Editor({ survey }) {
  const navigate = useNavigate();
  const save = useSaveSurvey();
  const close = useCloseSurvey();
  const reopen = useReopenSurvey();
  const duplicate = useDuplicateSurvey();
  const remove = useDeleteSurvey();

  const [draft, setDraft] = useState(() => toDraft(survey));
  const [openIds, setOpenIds] = useState(() => new Set());
  const [saveError, setSaveError] = useState('');
  const [previewOpen, setPreviewOpen] = useState(false);
  const [copyTitle, setCopyTitle] = useState('');
  const [confirm, setConfirm] = useState(null); // 'publish' | 'schedule' | 'delete' | 'close'
  const lastAddedRef = useRef(null);

  const saved = useMemo(() => toDraft(survey), [survey]);
  const dirty = JSON.stringify(draft) !== JSON.stringify(saved);
  const guard = useUnsavedChangesGuard(dirty);
  const busy = save.isPending || close.isPending || reopen.isPending;

  const set = (patch) => setDraft((d) => ({ ...d, ...patch }));
  const setContent = (updater) => {
    const next = typeof updater === 'function' ? updater(draft.content) : updater;
    const [fixed, dropped] = fixConditions(next);
    if (dropped) {
      showToast('info', `${dropped} condition${dropped === 1 ? '' : 's'} removed: ${dropped === 1 ? 'it' : 'they'} pointed at a question or answer that is no longer before ${dropped === 1 ? 'it' : 'them'}.`);
    }
    setDraft((d) => ({ ...d, content: fixed }));
  };

  const toggleOpen = (id, open) => setOpenIds((prev) => {
    const next = new Set(prev);
    if (open ?? !next.has(id)) next.add(id);
    else next.delete(id);
    return next;
  });

  // A new block opens and scrolls into view.
  useEffect(() => {
    if (!lastAddedRef.current) return;
    document.getElementById(`svb-${lastAddedRef.current}`)?.scrollIntoView({ behavior: 'smooth', block: 'center' });
    lastAddedRef.current = null;
  }, [draft.content.length]);

  const addBlock = (type, kind) => {
    const block = newBlock(type, kind);
    lastAddedRef.current = block.id;
    setContent((c) => [...c, block]);
    toggleOpen(block.id, true);
  };

  const updateBlock = (id, patch) => setContent((c) => c.map((b) => (b.id === id ? { ...b, ...patch } : b)));
  const deleteBlock = (id) => setContent((c) => c.filter((b) => b.id !== id));
  const duplicateBlock = (id) => setContent((c) => {
    const index = c.findIndex((b) => b.id === id);
    if (index < 0) return c;
    const source = c[index];
    const copy = { ...source, id: newBlock('text').id };
    if (Array.isArray(source.options)) copy.options = source.options.map((o) => newOption(o.label));
    lastAddedRef.current = copy.id;
    return [...c.slice(0, index + 1), copy, ...c.slice(index + 1)];
  });

  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 4 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates })
  );

  const doSave = async ({ quiet = false } = {}) => {
    setSaveError('');
    try {
      const result = await save.mutateAsync({ id: survey.id, ...draft });
      setDraft(toDraft(result));
      if (!quiet) showToast('success', 'Survey saved.');
      return result;
    } catch (err) {
      setSaveError(errorText(err, 'Unable to save the survey'));
      showToast('error', errorText(err, 'Unable to save the survey'));
      return null;
    }
  };

  const doClose = async () => {
    try {
      await close.mutateAsync(survey.id);
      showToast('success', 'Survey closed. No more responses will be taken.');
      setConfirm(null);
    } catch (err) {
      showToast('error', errorText(err, 'Unable to close the survey'));
    }
  };

  const doReopen = async () => {
    try {
      await reopen.mutateAsync(survey.id);
      showToast('success', 'Survey reopened.');
    } catch (err) {
      showToast('error', errorText(err, 'Unable to reopen the survey'));
    }
  };

  const doDuplicate = async () => {
    const title = copyTitle.trim();
    if (!title || title.length > 200 || duplicate.isPending) return;
    try {
      const copy = await duplicate.mutateAsync({ id: survey.id, title });
      showToast('success', 'Copied. You are editing the copy.');
      navigate(`/surveys/${copy.id}/edit`);
    } catch (err) {
      showToast('error', errorText(err, 'Unable to copy the survey'));
    }
  };

  const doDelete = async () => {
    try {
      await remove.mutateAsync(survey.id);
      showToast('success', 'Survey deleted.');
      navigate('/surveys');
    } catch (err) {
      showToast('error', errorText(err, 'Unable to delete the survey'));
    }
  };

  const problem = publishProblem(draft);
  const numbers = questionNumbers(draft.content);

  return (
    <div className="bdc-page svb">
      <PageHeader
        crumbs={[{ label: 'Home', to: '/' }, { label: 'Surveys', to: '/surveys' }, { label: draft.title || 'Untitled survey' }]}
        icon="bi-ui-checks"
        title={draft.title || 'Untitled survey'}
        onBack={() => guard.requestLeave('/surveys')}
        actions={(
          <>
            {survey.state !== 'draft' ? (
              <button type="button" className="bdc-btn" onClick={() => guard.requestLeave(`/surveys/${survey.id}/responses`)}>
                <i className="bi bi-bar-chart" aria-hidden="true" />
                Responses ({survey.responseCount})
              </button>
            ) : null}
            <button type="button" className="bdc-btn" onClick={() => setPreviewOpen(true)}>
              <i className="bi bi-eye" aria-hidden="true" />
              Preview
            </button>
            <button
              type="button"
              className={`bdc-btn${dirty ? ' bdc-btn--primary' : ''}`}
              onClick={() => doSave()}
              disabled={!dirty || busy}
            >
              <i className="bi bi-floppy" aria-hidden="true" />
              {save.isPending ? 'Saving…' : dirty ? 'Save' : 'Saved'}
            </button>
            {!survey.roundOf ? (
              <button type="button" className="bdc-btn" onClick={() => setConfirm('schedule')} disabled={dirty || busy || Boolean(problem)} title={dirty ? 'Save your changes before setting a schedule.' : problem || undefined}>
                <i className="bi bi-arrow-repeat" aria-hidden="true" />
                {survey.schedule ? 'Edit schedule' : 'Repeat'}
              </button>
            ) : null}
            <div className="svb-header__send-actions">
              <SurveyLinkButton survey={survey} />
              {survey.state !== 'closed' ? (
                <button type="button" className="bdc-btn bdc-btn--primary" onClick={() => setConfirm('publish')} disabled={dirty || busy || Boolean(problem)} title={dirty ? 'Save your changes before sending.' : problem || undefined}>
                  <i className="bi bi-send" aria-hidden="true" />
                  Send out
                </button>
              ) : null}
              {survey.state === 'open' ? (
                <button type="button" className="bdc-btn" onClick={() => setConfirm('close')} disabled={busy}>
                  <i className="bi bi-stop-circle" aria-hidden="true" />
                  Close survey
                </button>
              ) : survey.state === 'closed' ? (
                <button type="button" className="bdc-btn" onClick={doReopen} disabled={busy}>
                  <i className="bi bi-arrow-counterclockwise" aria-hidden="true" />
                  Reopen
                </button>
              ) : null}
            </div>
          </>
        )}
      />

      <div className="svb-status">
        <span className={`svy-state svy-state--${survey.state}`}>{STATE_LABELS[survey.state]}</span>
        {draft.anonymous ? (
          <span className="svy-anon">
            <i className="bi bi-incognito" aria-hidden="true" />
            Anonymous
          </span>
        ) : null}
        {survey.state === 'draft' ? (
          <span>{dirty ? 'Save your changes before sending.' : problem ? `Before it can go out: ${problem.charAt(0).toLowerCase()}${problem.slice(1)}` : 'Ready to send out.'}</span>
        ) : (
          <span>
            {(survey.allowLinkResponses || survey.audience === 'link') && survey.audience !== 'all' ? `${survey.responseCount} responses` : `${survey.responseCount} of ${survey.recipientCount} responded`}
            {survey.closesOn ? ` · ${survey.state === 'closed' ? 'closed' : 'closes'} ${formatDate(survey.closesOn)}` : ''}
          </span>
        )}
        {survey.schedule ? (
          <span className="svy-repeat">
            <i className="bi bi-arrow-repeat" aria-hidden="true" />
            {scheduleLabel(survey.schedule.everyMonths)}, next {formatDate(survey.schedule.nextRunOn)}
          </span>
        ) : null}
        {survey.roundOf ? (
          <span className="svy-repeat">
            <i className="bi bi-arrow-repeat" aria-hidden="true" />
            A round of {survey.roundOf.title}
          </span>
        ) : null}
        {dirty ? <span className="svb-status__unsaved">Unsaved changes</span> : null}
      </div>

      {saveError ? <Message type="error" showIcon closable onClose={() => setSaveError('')}>{saveError}</Message> : null}
      {survey.schedule?.lastError ? (
        <Message type="error" showIcon>
          The round due {formatDate(survey.schedule.nextRunOn)} couldn&rsquo;t go out: {survey.schedule.lastError} It tries again every hour.
        </Message>
      ) : null}
      {survey.roundOf ? (
        <Message type="info" showIcon>
          Changes here only affect this round. To change later rounds, edit {survey.roundOf.title}.
        </Message>
      ) : null}
      {survey.state !== 'draft' && survey.responseCount > 0 ? (
        <Message type="warning" showIcon>
          People have already responded. Changing or removing questions can leave their earlier answers behind.
        </Message>
      ) : null}

      <Section
        title="Settings"
        aside={(
          <div className="svb-aside">
            <button type="button" className="bdc-btn bdc-btn--sm" onClick={() => {
              setCopyTitle(`${survey.title || 'Untitled survey'} (copy)`.slice(0, 200));
              setConfirm('duplicate');
            }} disabled={duplicate.isPending}>
              <i className="bi bi-files" aria-hidden="true" />
              Duplicate
            </button>
            <button type="button" className="bdc-btn bdc-btn--sm bdc-btn--danger" onClick={() => setConfirm('delete')}>
              <i className="bi bi-trash" aria-hidden="true" />
              Delete
            </button>
          </div>
        )}
      >
        <div className="svb-settings">
          <label className="svb-field svb-field--wide">
            <span className="bdc-modal__label">Title</span>
            <Input value={draft.title} onChange={(v) => set({ title: v })} maxLength={200} placeholder="Remote work check-in" />
          </label>
          <label className="svb-field svb-field--wide">
            <span className="bdc-modal__label">Description (optional)</span>
            <Input
              as="textarea"
              rows={2}
              value={draft.description}
              onChange={(v) => set({ description: v })}
              placeholder="What the survey is for and how long it takes."
            />
          </label>
          <div className="svb-field svb-anon">
            <label className="svb-anon__toggle">
              <Toggle
                checked={draft.anonymous}
                onChange={(checked) => set({ anonymous: checked })}
                disabled={survey.state !== 'draft'}
              />
              <span>
                <strong>Anonymous</strong>
                <span className="svb-hint">
                  {survey.state !== 'draft'
                    ? `Fixed now the survey has been sent out${draft.anonymous ? ': answers are not linked to anyone.' : '.'}`
                    : 'Answers are stored apart from who sent them, so nobody can see who said what. You still see who has responded. People see a note saying so at the top of the survey, and can\'t change their answers once sent.'}
                </span>
              </span>
            </label>
            {draft.anonymous && hasSignature(draft.content) ? (
              <Message type="warning" showIcon>
                An anonymous survey can&rsquo;t ask for a signature, since a signature is a name. Remove the signature
                question before saving.
              </Message>
            ) : null}
          </div>
          <div className="svb-field">
            <span className="bdc-modal__label">Stops taking responses</span>
            <DatePicker
              block
              oneTap
              format="MM/dd/yyyy"
              placeholder="No closing date"
              value={fromIsoDate(draft.closesOn)}
              onChange={(v) => set({ closesOn: toIsoDate(v) })}
              shouldDisableDate={(date) => toIsoDate(date) < toIsoDate(new Date())}
            />
            <span className="svb-hint">
              {draft.closesOn ? `Responses are taken through the end of ${formatDate(draft.closesOn)}.` : 'Open until you close it.'}
            </span>
          </div>
          <div className="svb-field svb-field--wide svb-anon">
            <label className="svb-anon__toggle">
              <Toggle checked={draft.shuffleQuestions} onChange={(checked) => set({ shuffleQuestions: checked })} />
              <span>
                <strong>Shuffle questions</strong>
                <span className="svb-hint">
                  Each person gets the questions in their own random order, and keeps it if they come back. Text, links
                  and images stay with the question after them, and a question with a condition stays after the one it
                  depends on. This page, the preview and responses keep the order above.
                </span>
              </span>
            </label>
          </div>
        </div>
      </Section>

      <Section title="Questions and content" count={draft.content.length}>
        {draft.content.length === 0 ? (
          <div className="svb-blocks">
            <div className="svb-empty">
              Nothing in the survey yet. Add a question or some text below. Text, links and images sit between
              questions to explain them.
            </div>
          </div>
        ) : (
          <DndContext
            sensors={sensors}
            collisionDetection={closestCenter}
            modifiers={[restrictToVerticalAxis, restrictToParentElement]}
            onDragEnd={({ active, over }) => {
              if (!over || active.id === over.id) return;
              setContent((c) => arrayMove(c, c.findIndex((b) => b.id === active.id), c.findIndex((b) => b.id === over.id)));
            }}
          >
            <SortableContext items={draft.content.map((b) => b.id)} strategy={verticalListSortingStrategy}>
              <div className="svb-blocks">
                {draft.content.map((block, index) => (
                  <BlockRow
                    key={block.id}
                    block={block}
                    index={index}
                    number={numbers.get(block.id)}
                    content={draft.content}
                    surveyId={survey.id}
                    open={openIds.has(block.id)}
                    onToggle={() => toggleOpen(block.id)}
                    onChange={(patch) => updateBlock(block.id, patch)}
                    onDuplicate={() => duplicateBlock(block.id)}
                    onDelete={() => deleteBlock(block.id)}
                  />
                ))}
              </div>
            </SortableContext>
          </DndContext>
        )}

        <div className="svb-add">
          <div className="svb-add__group">
            <span className="svb-add__label">Add a question</span>
            {QUESTION_KINDS.filter((k) => !(draft.anonymous && k.value === 'signature')).map((k) => (
              <button key={k.value} type="button" className="svb-add__btn" onClick={() => addBlock('question', k.value)} title={k.hint}>
                <i className={`bi ${k.icon}`} aria-hidden="true" />
                {k.label}
              </button>
            ))}
          </div>
          <div className="svb-add__group">
            <span className="svb-add__label">Add content</span>
            {CONTENT_TYPES.map((t) => (
              <button key={t.value} type="button" className="svb-add__btn" onClick={() => addBlock(t.value)}>
                <i className={`bi ${t.icon}`} aria-hidden="true" />
                {t.label}
              </button>
            ))}
          </div>
        </div>
      </Section>

      <PreviewModal open={previewOpen} onClose={() => setPreviewOpen(false)} draft={draft} surveyId={survey.id} />

      {confirm === 'publish' ? <SendSurveyModal survey={survey} onClose={() => setConfirm(null)} /> : null}
      {confirm === 'schedule' ? <SurveyScheduleModal survey={survey} onClose={() => setConfirm(null)} /> : null}

      <Modal open={confirm === 'close'} onClose={() => setConfirm(null)} size="xs">
        <Modal.Header>
          <Modal.Title>Close this survey now?</Modal.Title>
        </Modal.Header>
        <Modal.Body>No more responses will be taken. The responses so far are kept, and you can reopen it later.</Modal.Body>
        <Modal.Footer>
          <Button onClick={() => setConfirm(null)}>Cancel</Button>
          <Button variant="primary" onClick={doClose} loading={close.isPending}>Close survey</Button>
        </Modal.Footer>
      </Modal>

      <Modal open={confirm === 'duplicate'} onClose={duplicate.isPending ? undefined : () => setConfirm(null)} size="xs">
        <Modal.Header closeButton={!duplicate.isPending}>
          <Modal.Title>Duplicate this survey?</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <div className="svb-copy-fields">
            <p>Create a new draft copy of <strong>{survey.title || 'Untitled survey'}</strong>? Responses and recipients are not copied.</p>
            <label className="svb-field">
              <span className="bdc-modal__label">Copy name</span>
              <Input value={copyTitle} onChange={setCopyTitle} maxLength={200} disabled={duplicate.isPending} autoFocus aria-label="Copy name" />
            </label>
            {dirty ? <p>Unsaved changes will not be included and will be discarded when you open the copy.</p> : null}
          </div>
        </Modal.Body>
        <Modal.Footer>
          <Button onClick={() => setConfirm(null)} disabled={duplicate.isPending}>Cancel</Button>
          <Button variant="primary" onClick={doDuplicate} loading={duplicate.isPending} disabled={duplicate.isPending || !copyTitle.trim()}>Duplicate</Button>
        </Modal.Footer>
      </Modal>

      <Modal open={confirm === 'delete'} onClose={() => setConfirm(null)} size="xs">
        <Modal.Header>
          <Modal.Title>Delete this survey?</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <strong>{draft.title || 'Untitled survey'}</strong> and {survey.responseCount ? `its ${survey.responseCount} response${survey.responseCount === 1 ? '' : 's'}` : 'everything in it'} will be
          deleted for good.
          {survey.schedule ? ' It stops repeating; rounds already sent out and their responses are kept.' : ''}
        </Modal.Body>
        <Modal.Footer>
          <Button onClick={() => setConfirm(null)}>Cancel</Button>
          <Button variant="danger-solid" onClick={doDelete} loading={remove.isPending}>Delete survey</Button>
        </Modal.Footer>
      </Modal>

      <Modal open={!!guard.pendingTo} onClose={guard.stay} size="xs">
        <Modal.Header>
          <Modal.Title>Leave without saving?</Modal.Title>
        </Modal.Header>
        <Modal.Body>This survey has changes that are not saved. Leaving now loses them.</Modal.Body>
        <Modal.Footer>
          <Button onClick={guard.stay}>Stay here</Button>
          <Button variant="danger-solid" onClick={guard.leave}>Leave without saving</Button>
        </Modal.Footer>
      </Modal>
    </div>
  );
}

// ---- One block in the stack: its summary, and its editor when open ----

const summaryOf = (block) => {
  if (block.type === 'question') return block.prompt || 'Untitled question';
  if (block.type === 'text') return block.text ? block.text.split('\n')[0] : 'Empty text';
  if (block.type === 'link') return block.label || block.url || 'No address yet';
  if (block.type === 'image') return block.caption || block.alt || (block.assetId ? 'Uploaded image' : block.url || 'No image yet');
  return '';
};

function BlockRow({ block, index, number, content, surveyId, open, onToggle, onChange, onDuplicate, onDelete }) {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id: block.id });
  const condition = describeCondition(block.condition, content);
  const options = Array.isArray(block.options) ? block.options.length : 0;

  return (
    <div
      ref={setNodeRef}
      id={`svb-${block.id}`}
      style={{ transform: CSS.Transform.toString(transform), transition }}
      className={`svb-block${isDragging ? ' is-dragging' : ''}${open ? ' is-open' : ''}`}
    >
      <div className="svb-block__row">
        <div className="svb-block__order">
          <button type="button" className="svb-block__handle" aria-label={`Drag ${blockLabel(block)} ${index + 1}`} {...attributes} {...listeners}>
            <i className="bi bi-grip-vertical" aria-hidden="true" />
          </button>
          <span className="svb-block__position">{number ? `Q${number}` : index + 1}</span>
        </div>

        <button type="button" className="svb-block__main" onClick={onToggle} aria-expanded={open}>
          <span className="svb-block__titlerow">
            <span className={`svb-kind${block.type === 'question' ? ' is-question' : ''}`}>
              <i className={`bi ${blockIcon(block)}`} aria-hidden="true" />
              {blockLabel(block)}
            </span>
            {block.type === 'question' && block.required ? <span className="svb-tag">Required</span> : null}
            {options ? <span className="svb-tag svb-tag--muted">{options} options</span> : null}
            {block.kind === 'choice' && block.multiple ? <span className="svb-tag svb-tag--muted">Pick several</span> : null}
          </span>
          <span className={`svb-block__summary${block.type === 'question' ? ' is-question' : ''}`}>{summaryOf(block)}</span>
          {condition ? (
            <span className="svb-block__condition">
              <i className="bi bi-signpost-split" aria-hidden="true" />
              {condition}
            </span>
          ) : null}
        </button>

        <div className="svb-block__actions">
          <button type="button" className="bdc-btn bdc-btn--sm" onClick={onToggle}>
            <i className={`bi ${open ? 'bi-check-lg' : 'bi-pencil'}`} aria-hidden="true" />
            {open ? 'Done' : 'Edit'}
          </button>
          <button type="button" className="svb-iconbtn" onClick={onDuplicate} aria-label="Duplicate" title="Duplicate">
            <i className="bi bi-copy" aria-hidden="true" />
          </button>
          <button type="button" className="svb-iconbtn svb-iconbtn--danger" onClick={onDelete} aria-label="Delete" title="Delete">
            <i className="bi bi-trash" aria-hidden="true" />
          </button>
        </div>
      </div>

      <div className={`svb-collapse${open ? ' is-open' : ''}`} inert={!open}>
        <div className="svb-collapse__inner">
          <div className="svb-editor">
            <BlockEditor block={block} content={content} surveyId={surveyId} onChange={onChange} />
          </div>
        </div>
      </div>
    </div>
  );
}

function BlockEditor({ block, content, surveyId, onChange }) {
  const earlierQuestions = content.slice(0, content.findIndex((b) => b.id === block.id)).filter((b) => b.type === 'question');

  return (
    <div className="svb-editor__fields">
      {block.type === 'text' ? (
        <label className="svb-field svb-field--wide">
          <span className="bdc-modal__label">Text</span>
          <Input as="textarea" rows={5} value={block.text} onChange={(v) => onChange({ text: v })} placeholder="Explain what the next questions are about." />
        </label>
      ) : null}

      {block.type === 'link' ? (
        <>
          <label className="svb-field">
            <span className="bdc-modal__label">Web address</span>
            <Input value={block.url} onChange={(v) => onChange({ url: v })} placeholder="https://…" />
          </label>
          <label className="svb-field">
            <span className="bdc-modal__label">Link text (optional)</span>
            <Input value={block.label} onChange={(v) => onChange({ label: v })} placeholder="Read the remote work policy" />
          </label>
        </>
      ) : null}

      {block.type === 'image' ? <ImageEditor block={block} surveyId={surveyId} onChange={onChange} /> : null}

      {block.type === 'question' ? <QuestionEditor block={block} onChange={onChange} /> : null}

      <ConditionEditor block={block} questions={earlierQuestions} content={content} onChange={onChange} />
    </div>
  );
}

function QuestionEditor({ block, onChange }) {
  const changeKind = (kind) => {
    if (!kind || kind === block.kind) return;
    const fresh = newBlock('question', kind);
    const keepOptions = ['choice', 'ranking'].includes(kind) && Array.isArray(block.options) ? { options: block.options } : {};
    onChange({ ...fresh, id: block.id, prompt: block.prompt, help: block.help, required: block.required, condition: block.condition, ...keepOptions });
  };

  return (
    <>
      <label className="svb-field svb-field--wide">
        <span className="bdc-modal__label">Question</span>
        <Input value={block.prompt} onChange={(v) => onChange({ prompt: v })} placeholder="How many days a week do you work from home?" />
      </label>
      <label className="svb-field">
        <span className="bdc-modal__label">Answer type</span>
        <SelectPicker
          block
          searchable={false}
          cleanable={false}
          data={QUESTION_KINDS.map((k) => ({ value: k.value, label: k.label, icon: k.icon }))}
          value={block.kind}
          onChange={changeKind}
          renderMenuItem={(label, item) => (
            <span className="svb-kindopt">
              <i className={`bi ${item.icon}`} aria-hidden="true" />
              {label}
            </span>
          )}
        />
        <span className="svb-hint">{kindInfo(block.kind)?.hint}</span>
      </label>
      <div className="svb-field svb-field--check">
        <Checkbox checked={block.required} onChange={(_v, checked) => onChange({ required: checked })}>
          An answer is required
        </Checkbox>
      </div>
      <label className="svb-field svb-field--wide">
        <span className="bdc-modal__label">Help text (optional)</span>
        <Input as="textarea" rows={2} value={block.help} onChange={(v) => onChange({ help: v })} placeholder="Anything that helps them answer." />
      </label>

      {block.kind === 'choice' || block.kind === 'ranking' ? (
        <div className="svb-field svb-field--wide">
          <span className="bdc-modal__label">{block.kind === 'ranking' ? 'Options to put in order' : 'Choices'}</span>
          <OptionsEditor options={block.options || []} onChange={(options) => onChange({ options })} />
          {block.kind === 'choice' ? (
            <Checkbox checked={block.multiple} onChange={(_v, checked) => onChange({ multiple: checked })}>
              Let them pick more than one
            </Checkbox>
          ) : null}
        </div>
      ) : null}

      {block.kind === 'text' ? (
        <div className="svb-field svb-field--wide">
          <span className="bdc-modal__label">Answer length</span>
          <div className="svb-segment" role="radiogroup" aria-label="Answer length">
            {TEXT_LENGTHS.map((option) => (
              <button
                key={option.label}
                type="button"
                role="radio"
                aria-checked={Boolean(block.multiline) === option.multiline}
                className={`svb-segment__option${Boolean(block.multiline) === option.multiline ? ' is-active' : ''}`}
                onClick={() => onChange({ multiline: option.multiline })}
              >
                {option.label}
              </button>
            ))}
          </div>
          <span className="svb-hint">
            {block.multiline
              ? 'A paragraph box. Enter starts a new line, and the box grows as they type.'
              : 'One line of text. Long answers wrap so they can still see all of it.'}
          </span>
        </div>
      ) : null}

      {block.kind === 'signature' ? (
        <label className="svb-field svb-field--wide">
          <span className="bdc-modal__label">What they are attesting to</span>
          <Input
            as="textarea"
            rows={3}
            value={block.statement}
            onChange={(v) => onChange({ statement: v })}
            placeholder="I confirm I have read and understood the remote work policy."
          />
          <span className="svb-hint">They sign by typing their full name under this statement.</span>
        </label>
      ) : null}

      {block.kind === 'rating' ? <span className="svb-hint svb-field--wide">Answered with one to five stars.</span> : null}
    </>
  );
}

const TEXT_LENGTHS = [
  { label: 'Single line', multiline: false },
  { label: 'Multiple lines', multiline: true },
];

function OptionsEditor({ options, onChange }) {
  const update = (id, label) => onChange(options.map((o) => (o.id === id ? { ...o, label } : o)));
  const move = (index, delta) => {
    const to = index + delta;
    if (to < 0 || to >= options.length) return;
    onChange(arrayMove(options, index, to));
  };
  return (
    <div className="svb-options">
      {options.map((option, index) => (
        <div key={option.id} className="svb-option">
          <span className="svb-option__n">{index + 1}</span>
          <Input
            value={option.label}
            onChange={(v) => update(option.id, v)}
            placeholder={`Option ${index + 1}`}
            onKeyDown={(e) => {
              if (e.key === 'Enter' && index === options.length - 1) {
                e.preventDefault();
                onChange([...options, newOption('')]);
              }
            }}
          />
          <button type="button" className="svb-iconbtn" onClick={() => move(index, -1)} disabled={index === 0} aria-label="Move up">
            <i className="bi bi-chevron-up" aria-hidden="true" />
          </button>
          <button type="button" className="svb-iconbtn" onClick={() => move(index, 1)} disabled={index === options.length - 1} aria-label="Move down">
            <i className="bi bi-chevron-down" aria-hidden="true" />
          </button>
          <button
            type="button"
            className="svb-iconbtn svb-iconbtn--danger"
            onClick={() => onChange(options.filter((o) => o.id !== option.id))}
            disabled={options.length <= 2}
            aria-label={`Remove option ${index + 1}`}
          >
            <i className="bi bi-x-lg" aria-hidden="true" />
          </button>
        </div>
      ))}
      <button type="button" className="bdc-btn bdc-btn--sm" onClick={() => onChange([...options, newOption('')])} disabled={options.length >= 50}>
        <i className="bi bi-plus-lg" aria-hidden="true" />
        Add option
      </button>
    </div>
  );
}

function ImageEditor({ block, surveyId, onChange }) {
  const upload = useUploadSurveyImage();
  const inputRef = useRef(null);
  const preview = useSurveyImageUrl(block.assetId ? surveyId : null, block.assetId);
  const src = block.assetId ? preview.url : block.url;

  const pick = async (file) => {
    if (!file) return;
    try {
      const asset = await upload.mutateAsync({ surveyId, file });
      onChange({ assetId: asset.id, url: '' });
      showToast('success', 'Image uploaded. Save the survey to keep it.');
    } catch (err) {
      showToast('error', errorText(err, 'Unable to upload that image'));
    } finally {
      if (inputRef.current) inputRef.current.value = '';
    }
  };

  return (
    <>
      <div className="svb-field svb-field--wide">
        <span className="bdc-modal__label">Image</span>
        <div className="svb-image">
          <div className="svb-image__frame">
            {src ? <img src={src} alt={block.alt || ''} /> : <i className="bi bi-image" aria-hidden="true" />}
          </div>
          <div className="svb-image__controls">
            <input
              ref={inputRef}
              type="file"
              accept="image/png,image/jpeg,image/gif,image/webp"
              hidden
              onChange={(e) => pick(e.target.files?.[0])}
            />
            <button type="button" className="bdc-btn bdc-btn--sm" onClick={() => inputRef.current?.click()} disabled={upload.isPending}>
              <i className="bi bi-upload" aria-hidden="true" />
              {upload.isPending ? 'Uploading…' : block.assetId ? 'Replace image' : 'Upload image'}
            </button>
            <span className="svb-hint">PNG, JPG, GIF or WebP, up to 5 MB. Or use an image on the web:</span>
            <Input
              value={block.url}
              onChange={(v) => onChange({ url: v, assetId: null })}
              placeholder="https://…/picture.png"
              disabled={Boolean(block.assetId)}
            />
            {block.assetId ? (
              <button type="button" className="svb-linkbtn" onClick={() => onChange({ assetId: null })}>
                Remove the uploaded image
              </button>
            ) : null}
          </div>
        </div>
      </div>
      <label className="svb-field">
        <span className="bdc-modal__label">Description for screen readers</span>
        <Input value={block.alt} onChange={(v) => onChange({ alt: v })} placeholder="What the image shows" />
      </label>
      <label className="svb-field">
        <span className="bdc-modal__label">Caption (optional)</span>
        <Input value={block.caption} onChange={(v) => onChange({ caption: v })} />
      </label>
    </>
  );
}

function ConditionEditor({ block, questions, content, onChange }) {
  const condition = block.condition;
  const on = Boolean(condition?.questionId);
  const source = questions.find((q) => q.id === condition?.questionId) || null;
  const numbers = questionNumbers(content);

  if (!questions.length) {
    return (
      <div className="svb-condition svb-field--wide">
        <span className="svb-hint">
          <i className="bi bi-signpost-split" aria-hidden="true" /> Questions after this one can be shown only when it is answered a certain way.
        </span>
      </div>
    );
  }

  const start = (checked) => {
    if (!checked) return onChange({ condition: null });
    const q = questions[questions.length - 1];
    const op = conditionOps(q)[0].value;
    return onChange({ condition: { questionId: q.id, op, value: q.kind === 'choice' ? q.options[0]?.id : q.kind === 'rating' ? 3 : null } });
  };

  const setSource = (id) => {
    const q = questions.find((x) => x.id === id);
    if (!q) return;
    const op = conditionOps(q)[0].value;
    onChange({ condition: { questionId: q.id, op, value: q.kind === 'choice' ? q.options[0]?.id : q.kind === 'rating' ? 3 : null } });
  };

  return (
    <div className="svb-condition svb-field--wide">
      <label className="svb-condition__toggle">
        <Toggle size="sm" checked={on} onChange={start} />
        <span>Show this only when an earlier answer calls for it</span>
      </label>
      <div className={`svb-collapse${on ? ' is-open' : ''}`} inert={!on}>
        <div className="svb-collapse__inner">
          <div className="svb-condition__row">
            <span className="svb-condition__word">Only if</span>
            <SelectPicker
              searchable={false}
              cleanable={false}
              style={{ minWidth: 220 }}
              data={questions.map((q) => ({ value: q.id, label: `Q${numbers.get(q.id)}. ${q.prompt || 'Untitled question'}` }))}
              value={condition?.questionId || null}
              onChange={setSource}
            />
            {source ? (
              <SelectPicker
                searchable={false}
                cleanable={false}
                style={{ minWidth: 150 }}
                data={conditionOps(source).map((o) => ({ value: o.value, label: o.label }))}
                value={condition.op}
                onChange={(op) => onChange({
                  condition: {
                    ...condition,
                    op,
                    value: opNeedsValue(op) ? condition.value ?? (source.kind === 'choice' ? source.options[0]?.id : 3) : null,
                  },
                })}
              />
            ) : null}
            {source && opNeedsValue(condition.op) ? (
              source.kind === 'choice' ? (
                <SelectPicker
                  searchable={false}
                  cleanable={false}
                  style={{ minWidth: 160 }}
                  data={source.options.map((o) => ({ value: o.id, label: o.label || 'Untitled option' }))}
                  value={condition.value}
                  onChange={(value) => onChange({ condition: { ...condition, value } })}
                />
              ) : (
                <SelectPicker
                  searchable={false}
                  cleanable={false}
                  style={{ minWidth: 120 }}
                  data={[1, 2, 3, 4, 5].map((n) => ({ value: n, label: `${n} star${n === 1 ? '' : 's'}` }))}
                  value={Number(condition.value) || 3}
                  onChange={(value) => onChange({ condition: { ...condition, value } })}
                />
              )
            ) : null}
          </div>
        </div>
      </div>
    </div>
  );
}

// ---- Preview: the survey as it will be taken, answers not kept ----

function PreviewModal({ open, onClose, draft, surveyId }) {
  const [answers, setAnswers] = useState({});
  return (
    <Modal open={open} onClose={onClose} size="md" overflow>
      <Modal.Header>
        <Modal.Title>Preview</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <div className="svb-preview">
          <Message type="info" showIcon>
            This is how it looks to the people it goes to. Try the answers to check the conditions; nothing is saved.
          </Message>
          <h2 className="svb-preview__title">{draft.title || 'Untitled survey'}</h2>
          {draft.anonymous ? (
            <div className="svb-preview__anon" role="note">
              <i className="bi bi-incognito" aria-hidden="true" />
              <span>
                <strong>Anonymous survey.</strong> {ANONYMOUS_NOTE.replace(/^This survey is anonymous\. /, '')}
              </span>
            </div>
          ) : null}
          {draft.description ? <p className="svb-preview__desc">{draft.description}</p> : null}
          {draft.content.length ? (
            <SurveyForm surveyId={surveyId} content={draft.content} answers={answers} onChange={setAnswers} />
          ) : (
            <p className="svb-hint">Nothing in the survey yet.</p>
          )}
        </div>
      </Modal.Body>
      <Modal.Footer>
        <Button onClick={() => setAnswers({})}>Clear answers</Button>
        <Button variant="primary" onClick={onClose}>Close preview</Button>
      </Modal.Footer>
    </Modal>
  );
}
